import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { TicketIcon, ClipboardDocumentCheckIcon, ClipboardIcon, BuildingStorefrontIcon, GlobeAltIcon, ArrowRightIcon } from "@heroicons/react/24/outline";

const CouponCard = ({ c }) => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const isGlobal = (c.scope || "global") === "global";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(c.code);
      setCopied(true);
      toast.success(`Copied ${c.code}`);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Could not copy"); }
  };

  const applyAtCheckout = () => {
    localStorage.setItem("xm_coupon", c.code);
    toast.success(`${c.code} will apply at checkout`);
    navigate("/cart");
  };

  return (
    <Card className="relative overflow-hidden rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] flex" data-testid={`coupon-${c.code}`}>
      {/* Left value stub */}
      <div className="w-24 shrink-0 bg-gradient-to-br from-orange-500 to-amber-500 text-white flex flex-col items-center justify-center py-6">
        <TicketIcon className="w-6 h-6 mb-1" />
        <span className="font-black text-2xl leading-none">{Math.round(c.percent_off)}%</span>
        <span className="text-[11px] opacity-90">OFF</span>
      </div>
      {/* Perforation */}
      <div className="relative w-0">
        <div className="absolute inset-y-0 -left-2 flex flex-col justify-between py-1">
          {Array.from({ length: 6 }).map((_, i) => <span key={i} className="w-3 h-3 rounded-full bg-muted/60 dark:bg-background" />)}
        </div>
      </div>
      {/* Body */}
      <div className="flex-1 p-4 pl-5 min-w-0">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {isGlobal ? <GlobeAltIcon className="w-4 h-4" /> : <BuildingStorefrontIcon className="w-4 h-4" />}
          <span className="truncate">{isGlobal ? "Platform-wide" : (c.seller_name || "Store")}</span>
        </div>
        <p className="font-mono font-bold text-lg tracking-wider mt-1 truncate" data-testid={`coupon-code-${c.code}`}>{c.code}</p>
        <div className="flex flex-wrap gap-2 mt-2">
          <Button size="sm" variant={copied ? "secondary" : "outline"} onClick={copy}
            className="rounded-full" data-testid={`coupon-copy-${c.code}`}>
            {copied ? <><ClipboardDocumentCheckIcon className="w-4 h-4 mr-1.5 text-green-600" />Copied</> : <><ClipboardIcon className="w-4 h-4 mr-1.5" />Copy</>}
          </Button>
          <Button size="sm" onClick={applyAtCheckout} className="rounded-full bg-orange-500 hover:bg-orange-600" data-testid={`coupon-apply-${c.code}`}>
            Apply at checkout<ArrowRightIcon className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export const BuyerCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/buyer/coupons")
      .then((r) => setCoupons(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="grid md:grid-cols-2 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 rounded-2xl bg-muted/40 animate-pulse" />)}</div>;

  if (coupons.length === 0) {
    return (
      <Card className="p-10 text-center rounded-2xl" data-testid="buyer-coupons-empty">
        <TicketIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
        <p className="font-semibold">No coupons available right now</p>
        <p className="text-sm text-muted-foreground mt-1">Check back soon — new deals from your favourite stores land here.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="buyer-coupons">
      <p className="text-sm text-muted-foreground">Copy a code and paste it at checkout to save on your next order.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {coupons.map((c) => <CouponCard key={c.coupon_id || c.code} c={c} />)}
      </div>
    </div>
  );
};
